import { useTranslation } from 'react-i18next';

import { OrganizationRoleV2 } from '../../../types/organizationTeam';
import { TableActionButton } from '../components/cells';

/** Radio card for picking an organization role (add-member and attach dialogs). */
export const RoleChoice = ({
  role,
  checked,
  onSelect,
  name,
}: {
  role: OrganizationRoleV2;
  checked: boolean;
  onSelect: () => void;
  name: string;
}) => {
  const { t } = useTranslation();
  return (
    <label className={`team-role-choice${checked ? ' is-checked' : ''}`}>
      <input type="radio" name={name} checked={checked} onChange={onSelect} />
      <span className="team-role-choice-body">
        <span className="team-role-choice-name">{role.name}</span>
        <span className="team-role-choice-perms">
          {role.permissions.length
            ? role.permissions.map((p) => p.title).join(', ')
            : t('dashboard.team.roles.noPermissions')}
        </span>
      </span>
    </label>
  );
};

interface TeamRolesPanelProps {
  roles: OrganizationRoleV2[];
  onCreate: () => void;
  onEdit: (role: OrganizationRoleV2) => void;
  onDelete: (role: OrganizationRoleV2) => void;
}

export const TeamRolesPanel = ({ roles, onCreate, onEdit, onDelete }: TeamRolesPanelProps) => {
  const { t } = useTranslation();
  return (
    <section className="team-panel" aria-labelledby="team-roles-title">
      <header className="team-panel-header">
        <h2 id="team-roles-title" className="team-panel-title">
          {t('dashboard.team.roles.title', { count: roles.length })}
        </h2>
        <button type="button" className="secondary-action-btn" onClick={onCreate}>
          {t('dashboard.team.roles.create')}
        </button>
      </header>
      {roles.length === 0 ? (
        <div className="team-first-role">
          <p className="team-first-role-title">{t('dashboard.team.firstRoleTitle')}</p>
          <p className="team-muted">{t('dashboard.team.firstRoleHint')}</p>
          <button type="button" className="primary-action-btn" onClick={onCreate}>
            {t('dashboard.team.roles.create')}
          </button>
        </div>
      ) : (
        <ul className="team-role-cards">
          {roles.map((r) => (
            <li key={r.id} className="team-role-card">
              <div className="team-role-card-head">
                <span className="team-role-card-name">{r.name}</span>
                <span className="team-muted">{t('dashboard.team.roles.memberCount', { count: r.memberCount })}</span>
                <span className="team-role-card-actions">
                  <TableActionButton icon="edit" onClick={() => onEdit(r)} ariaLabel={t('dashboard.roles.editAria', { name: r.name })} />
                  <TableActionButton
                    icon="delete"
                    onClick={() => onDelete(r)}
                    disabled={r.memberCount > 0}
                    title={r.memberCount > 0 ? t('dashboard.team.roles.cannotDeleteHeld') : t('dashboard.common.delete')}
                    ariaLabel={t('dashboard.roles.deleteAria', { name: r.name })}
                  />
                </span>
              </div>
              <div className="team-perm-chips">
                {r.permissions.length === 0 ? (
                  <span className="team-muted">{t('dashboard.team.roles.noPermissions')}</span>
                ) : (
                  r.permissions.map((p) => <span key={p.id} className="team-perm-chip">{p.title}</span>)
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
